import { supabase } from '@/integrations/supabase/client'
import { actionLogger } from './actionLogger'

export interface GenerationResult {
  processed: number
  successful: number
  failed: number
  skipped: number
  results: Array<{
    id: string
    success: boolean
    createdTransactionId?: string
    nextDate?: string
    error?: string | null
  }>
}

type GenerationOptions = {
  dryRun?: boolean
  dateOverride?: string
  userIds?: string[]
  seriesId?: string
  generateCount?: number
  upToDate?: string | null
}

const mapReportToResult = (report: any, fallbackId?: string): GenerationResult => {
  const processed = report?.processed ?? 0
  const successful = report?.successful ?? report?.generated ?? 0
  const failed = report?.failed ?? 0
  const skipped = report?.skipped ?? 0
  const entries = Array.isArray(report?.results || report?.data)
    ? (report.results || report.data)
    : []

  return {
    processed,
    successful,
    failed,
    skipped,
    results: entries.map((item: any) => ({
      id: item.id || fallbackId || 'unknown',
      success: item.success ?? true,
      createdTransactionId: item.createdTransactionId || item.id,
      nextDate: item.nextDate,
      error: item.error ?? null
    }))
  }
}

export async function generateRecurringTransactions(options?: GenerationOptions): Promise<GenerationResult> {
  try {
    const payload = options?.seriesId
      ? {
          seriesId: options.seriesId,
          generateCount: options.generateCount ?? 1,
          upToDate: options.upToDate ?? null
        }
      : {
          dryRun: options?.dryRun || false,
          dateOverride: options?.dateOverride,
          userIds: options?.userIds || null
        }

    const { data, error } = await supabase.functions.invoke('generate-recurring-transactions', {
      body: payload
    })

    if (error) {
      throw new Error(error.message || 'Recurring generation failed')
    }

    const report = data?.report || data || {}
    const result = mapReportToResult(report, options?.seriesId)

    actionLogger.info('Recurring Transactions Generated', `Processed ${result.processed} transaction(s)`, {
      category: 'transaction',
      details: {
        seriesId: options?.seriesId,
        processed: result.processed,
        successful: result.successful,
        failed: result.failed,
        skipped: result.skipped,
        dryRun: options?.dryRun
      }
    })

    return result
  } catch (error) {
    actionLogger.error('Generate Recurring Transactions Error', error.message, {
      category: 'error',
      details: { options }
    })
    throw error
  }
}

export async function generateRecurringTransactionsForSeries(
  seriesId: string,
  options?: { generateCount?: number; upToDate?: Date | string | null }
): Promise<GenerationResult> {
  const baseGenerateCount = options?.generateCount && options.generateCount > 0
    ? options.generateCount
    : 1

  const normalizedEnd = options?.upToDate
    ? typeof options.upToDate === 'string'
      ? options.upToDate
      : options.upToDate.toISOString().split('T')[0]
    : null

  const { data, error } = await supabase.rpc('fn_secure_generate_recurring_transactions', {
    p_parent_transaction_id: seriesId,
    p_generate_count: baseGenerateCount,
    p_max_count: baseGenerateCount,
    p_up_to_date: normalizedEnd
  })

  if (error) {
    throw new Error(error.message || 'Failed to generate recurring transactions for series')
  }

  const entries = Array.isArray(data) ? data : []

  return {
    processed: 1,
    successful: entries.length,
    failed: 0,
    skipped: 0,
    results: entries.map((item: any) => ({
      id: seriesId,
      success: true,
      createdTransactionId: item.created_transaction_id || item.createdTransactionId || item.id,
      nextDate: item.next_date || item.nextDate,
      error: null
    }))
  }
}
